import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, MapPin, Wine, PartyPopper, Trophy, ChevronDown } from "lucide-react";
import api from "@/lib/api";

const WORDS = ["Os", "rolês", "de", "Marília", "na", "palma", "da", "sua", "mão."];
const MARQUEE = ["Resenha", "Churras", "Adega aberta", "Festa", "Luau", "Society", "Promo do dia", "Show", "Atlética", "Beach tennis", "Feira", "Gelada"];

function useLiveCounts() {
  const [c, setC] = useState(null);
  useEffect(() => {
    const load = () => Promise.all([api.get("/events"), api.get("/adegas"), api.get("/games")])
      .then(([e, a, g]) => setC({ events: e.data.length, adegas: a.data.filter((x) => x.is_open).length, games: g.data.length }))
      .catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);
  return c;
}

function FloatCard({ icon: Icon, title, sub, className, delay, testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 40, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`absolute hidden xl:flex items-center gap-3 rounded-2xl px-4 py-3 bg-black/45 backdrop-blur-2xl border border-white/15 shadow-[0_20px_60px_rgba(0,0,0,0.6)] ${className}`} data-testid={testid}>
      <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay }} className="flex items-center gap-3">
        <span className="relative h-10 w-10 rounded-full bg-[#FFC700] text-black grid place-items-center shadow-[0_0_25px_rgba(255,199,0,0.7)]">
          <span className="absolute inset-0 rounded-full bg-[#FFC700] animate-ping opacity-40" /><Icon className="w-5 h-5 relative" />
        </span>
        <div><div className="text-sm font-bold">{title}</div><div className="text-[11px] text-white/60">{sub}</div></div>
      </motion.div>
    </motion.div>
  );
}

export default function LandingHero({ go }) {
  const ref = useRef(null);
  const counts = useLiveCounts();
  const live = counts ? [
    [counts.events, "rolê", "rolês"], [counts.adegas, "adega aberta", "adegas abertas"], [counts.games, "jogo", "jogos"],
  ].filter(([n]) => n > 0).map(([n, one, many]) => `${n} ${n === 1 ? one : many}`) : [];
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.35]);
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const mx = useMotionValue(50), my = useMotionValue(40);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 }), sy = useSpring(my, { stiffness: 60, damping: 20 });
  const spot = useTransform([sx, sy], ([x, v]) => `radial-gradient(600px circle at ${x}% ${v}%, rgba(255,199,0,0.18), transparent 60%)`);
  const tiltX = useTransform(sy, [0, 100], [4, -4]), tiltY = useTransform(sx, [0, 100], [-4, 4]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width) * 100);
    my.set(((e.clientY - r.top) / r.height) * 100);
  };

  return (
    <section ref={ref} id="topo" onMouseMove={onMove} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="landing-hero">
      <motion.img src="/hero-city.jpg" alt="Marília à noite" style={{ scale, y }} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-[#0d0d0d]" />
      <div className="absolute inset-0 [background:radial-gradient(ellipse_at_center,transparent_30%,rgba(0,0,0,0.85)_100%)]" />
      <motion.div className="absolute inset-0 pointer-events-none" style={{ background: spot }} />
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

      <FloatCard icon={Wine} title="Adega aberta agora" sub="a 450 m de você" className="left-[6%] top-[28%]" delay={1.4} testid="hero-float-adega" />
      <FloatCard icon={PartyPopper} title="Resenha rolando" sub="32 confirmados" className="right-[7%] top-[22%]" delay={1.7} testid="hero-float-role" />
      <FloatCard icon={Trophy} title="Society · faltam 2" sub="hoje 19:00" className="right-[12%] bottom-[22%]" delay={2} testid="hero-float-game" />

      <motion.div style={{ opacity: fade, rotateX: tiltX, rotateY: tiltY }} className="relative z-10 h-full max-w-5xl mx-auto px-5 flex flex-col items-center justify-center text-center [perspective:1000px]">
        {live.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#FFC700]/50 bg-black/40 backdrop-blur-xl text-[#FFC700] text-xs font-semibold mb-8" data-testid="hero-live-badge">
          <span className="relative h-2 w-2"><span className="absolute inset-0 rounded-full bg-[#FFC700] animate-ping" /><span className="absolute inset-0 rounded-full bg-[#FFC700]" /></span>
          AO VIVO EM MARÍLIA · {live.join(" · ")}
        </motion.div>
        )}
        <h1 className="heading text-5xl sm:text-6xl lg:text-8xl font-extrabold leading-[0.95] tracking-tight">
          {WORDS.map((w, i) => (
            <motion.span key={i} initial={{ opacity: 0, y: 60, filter: "blur(12px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ delay: 0.35 + i * 0.08, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className={`inline-block mr-[0.25em] ${i >= 5 ? "text-[#FFC700] drop-shadow-[0_0_40px_rgba(255,199,0,0.6)]" : ""}`}>{w}</motion.span>
          ))}
        </h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }} className="mt-8 text-base md:text-xl text-white/75 max-w-2xl">
          Descubra as melhores festas, bares e adegas da cidade ou coloque seu negócio no mapa dos rolês.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5 }} className="mt-10 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <button onClick={go("/login")} className="group relative h-16 px-9 rounded-full bg-[#FFC700] text-black font-extrabold text-base flex items-center justify-center gap-2 shadow-[0_0_50px_rgba(255,199,0,0.55)] hover:shadow-[0_0_80px_rgba(255,199,0,0.8)] hover:scale-[1.04] transition-all overflow-hidden" data-testid="hero-cta-curtir">
            <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/60 to-transparent" />
            <MapPin className="w-5 h-5 relative" /><span className="relative">Quero Achar um Rolê</span><ArrowRight className="w-5 h-5 relative group-hover:translate-x-1 transition-transform" />
          </button>
          <button onClick={go("/login?tipo=adega")} className="h-16 px-9 rounded-full border border-white/30 bg-white/5 backdrop-blur-xl font-semibold hover:border-[#FFC700] hover:text-[#FFC700] hover:bg-[#FFC700]/10 transition-colors" data-testid="hero-cta-adega">Sou Dono de Adega / Bar</button>
        </motion.div>
      </motion.div>

      <a href="#como-funciona" className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 text-white/60 flex flex-col items-center text-[10px] tracking-[0.3em]" data-testid="hero-scroll">
        ROLE PRA BAIXO <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}><ChevronDown className="w-5 h-5 mt-1" /></motion.span>
      </a>
      <div className="absolute bottom-0 inset-x-0 z-10 border-y border-[#FFC700]/20 bg-black/60 backdrop-blur-xl py-3 overflow-hidden">
        <motion.div className="flex gap-10 whitespace-nowrap w-max" animate={{ x: ["0%", "-50%"] }} transition={{ repeat: Infinity, duration: 30, ease: "linear" }}>
          {[...MARQUEE, ...MARQUEE].map((m, i) => <span key={i} className="heading text-sm font-bold uppercase tracking-widest text-white/70">{m} <span className="text-[#FFC700] mx-4">●</span></span>)}
        </motion.div>
      </div>
    </section>
  );
}
